import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Link } from "react-router";
import styled from "styled-components";

import ListBimClassesAssignedToCategory from "./ListBimClassesAssignedToCategory.graphql";
import ReadCategory from "./ReadCategory.graphql";
import UpsertCategory from "./UpsertCategory.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import { TAXONOMY_BASE } from "../../../../../constants";
import { Finder2 } from "../../../../../modules/finder";
import * as UI from "../../../../../modules/ui-kit";
import BimClassFinderPlugin from "../../../components/BimClassFinderPlugin";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";
import categoryDefinition from "../categoryDefinition";

const StyledAssignedBimClasses = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    & > ul {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        list-style: none;
        margin: 0;
        padding: 0;
    }

    & > ul > li {
        background: #0000000a;
        border-radius: 4px;

        &:hover button {
            opacity: 1;
        }

        a {
            color: inherit;
            overflow: hidden;
            text-decoration: none;
            text-overflow: ellipsis;
            white-space: nowrap;

            &:hover {
                text-decoration: underline;
            }
        }

        button {
            flex: none;
            opacity: .35;
            transition: opacity 200ms;
        }
    }

    .actions {
        display: flex;
        flex-direction: row;
        gap: 8px;
    }

    dialog {
        ul {
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin: 0;
            padding: 0;
        }
    }
`;

function toAssignment(bimClass) {
    return {
        id: bimClass.id,
        name: bimClass.data.name[0].text,
    };
}

function sameAssignments(a, b) {
    return a.length === b.length && a.every((item, index) => item.id === b[index].id);
}

export default function AssignedBimClasses(props) {
    const { guard, id } = props;

    const [Dialog, open] = UI.useDialog();

    const listBimClassesAssignedToCategory = useQuery(
        gql(ListBimClassesAssignedToCategory),
        {
            variables: {
                id,
            },
        },
    );

    const readCategory = useQuery(
        gql(ReadCategory),
        {
            variables: {
                id,
            },
        },
    );

    const [upsertCategory] = useMutation(
        gql(UpsertCategory),
        {
            // Both editors on this page build their payload from ReadCategory,
            // so the refetch has to land before another save reads it.
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(ListBimClassesAssignedToCategory),
                gql(ReadCategory),
            ],
            variables: {
                id,
            },
        },
    );

    const assignedOnServer = (listBimClassesAssignedToCategory.data?.category?.bimClasses ?? []).map(toAssignment);

    const notifications = useNotifications();

    const [assigned, setAssigned] = useState(undefined);
    const [saving, setSaving] = useState(false);



    // Re-seed when a different category is opened, or once the query resolves.
    useEffect(
        () => {
            if (listBimClassesAssignedToCategory.data) {
                setAssigned(
                    listBimClassesAssignedToCategory.data.category.bimClasses.map(toAssignment)
                );
            }
        },
        [id, listBimClassesAssignedToCategory.data],
    );

    const current = assigned ?? assignedOnServer;
    const dirty = !sameAssignments(current, assignedOnServer);

    async function save() {
        setSaving(true);

        try {
            const result = await upsertCategory(
                {
                    variables: {
                        definition: categoryDefinition(
                            readCategory.data,
                            {
                                bimClasses: current.map((bimClass) => ({ id: bimClass.id })),
                            },
                        ),
                    },
                }
            );

            if (result.data?.upsertCategory?.errors?.length) {
                notifyMutationErrors(notifications, result.data.upsertCategory.errors);
                return false;
            }

            notifications.success("Saved.");

            return true;
        } finally {
            setSaving(false);
        }
    }

    // Reset for this screen lives in CategoryDetail, outside this component's
    // markup, so it can only revert what an editor registers here.
    function reset() {
        setAssigned(assignedOnServer);
    }

    useUnsavedChangesEditor(guard, save, dirty, reset);

    if (listBimClassesAssignedToCategory.loading || readCategory.loading) return "loading";
    if (listBimClassesAssignedToCategory.error || readCategory.error) return "error";

    function remove(bimClassID) {
        setAssigned(current.filter((bimClass) => bimClass.id !== bimClassID));
    }

    // The dialog deliberately stays open so several BimClasses can be assigned
    // in one pass; the list behind it updates as they are picked.
    function add(bimClass) {
        if (current.some((assignment) => assignment.id === bimClass.id)) return;
        setAssigned([...current, bimClass]);
    }

    return (
        <StyledAssignedBimClasses>
            <UI.FieldDescription>
                {
                    current.length
                        ? `${ current.length } assigned`
                        : "Nothing assigned yet — this category stays in the tree so BimClasses can be added to it."
                }
            </UI.FieldDescription>
            <ul>
                {
                    // Display is sorted by name; the stored order is left alone so
                    // sorting cannot make the editor look dirty.
                    current
                        .toSorted((a, b) => a.name.localeCompare(b.name))
                        .map(
                            (bimClass) => (
                                <li key={ bimClass.id }>
                                    <UI.Card>
                                        <Link
                                            title={ bimClass.name }
                                            to={ `${ TAXONOMY_BASE }/BimClasses/${ bimClass.id }` }
                                        >
                                            { bimClass.name }
                                        </Link>
                                        <UI.Button
                                            $appearance="subtle"
                                            $size="icon"
                                            disabled={ saving }
                                            onClick={ () => remove(bimClass.id) }
                                            title={ `remove ${ bimClass.name }` }
                                        >
                                            <IoClose />
                                        </UI.Button>
                                    </UI.Card>
                                </li>
                            )
                        )
                }
            </ul>
            <UI.Button
                disabled={ saving }
                onClick={ open }
            >
                Add BimClass
            </UI.Button>
            <Dialog>
                <Finder2 placeholder="Search BimClasses">
                    <BimClassFinderPlugin
                        exclude={ current.map((bimClass) => bimClass.id) }
                        onSelect={ add }
                    />
                </Finder2>
            </Dialog>
        </StyledAssignedBimClasses>
    );
}
